import React, { useState } from 'react';
import { Mail } from 'lucide-react';

interface EmailTemplateModalProps {
  invitationSubject: string;
  invitationBody: string;
  reminderSubject: string;
  reminderBody: string;
  onSave: (template: {
    invitationSubject: string;
    invitationBody: string;
    reminderSubject: string;
    reminderBody: string;
  }) => void;
  onClose: () => void;
}

const EmailTemplateModal: React.FC<EmailTemplateModalProps> = ({
  invitationSubject: initialInvitationSubject,
  invitationBody: initialInvitationBody,
  reminderSubject: initialReminderSubject,
  reminderBody: initialReminderBody,
  onSave,
  onClose,
}) => {
  const defaultInvitationSubject = "You're invited to provide feedback";
  const defaultInvitationBody = "Hello {recipientName},\n\nYou have been invited to provide feedback.\n\nYour feedback is valuable and helps us improve our delivery process.";
  const defaultReminderSubject = "Reminder: Please complete your feedback";
  const defaultReminderBody = "Hello {recipientName},\n\nThis is a friendly reminder to complete your feedback.\n\nYour input is important to us. It only takes a few minutes to complete.";

  const allEmpty = !initialInvitationSubject && !initialInvitationBody && !initialReminderSubject && !initialReminderBody;

  const [invitationSubject, setInvitationSubject] = useState(allEmpty ? defaultInvitationSubject : initialInvitationSubject);
  const [invitationBody, setInvitationBody] = useState(allEmpty ? defaultInvitationBody : initialInvitationBody);
  const [reminderSubject, setReminderSubject] = useState(allEmpty ? defaultReminderSubject : initialReminderSubject);
  const [reminderBody, setReminderBody] = useState(allEmpty ? defaultReminderBody : initialReminderBody);

  const handleSave = () => {
    onSave({ invitationSubject, invitationBody, reminderSubject, reminderBody });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <Mail className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Email Templates</h2>
          </div>

          <div className="space-y-6">
            <div>
              <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">Invitation Email</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Subject</label>
                  <input
                    type="text"
                    value={invitationSubject}
                    onChange={(e) => setInvitationSubject(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Message</label>
                  <textarea
                    value={invitationBody}
                    onChange={(e) => setInvitationBody(e.target.value)}
                    rows={6}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white resize-vertical"
                  />
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">Reminder Email</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Subject</label>
                  <input
                    type="text"
                    value={reminderSubject}
                    onChange={(e) => setReminderSubject(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Message</label>
                  <textarea
                    value={reminderBody}
                    onChange={(e) => setReminderBody(e.target.value)}
                    rows={6}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white resize-vertical"
                  />
                </div>
              </div>
            </div>
          </div>

          <p className="mt-4 text-xs text-gray-500 dark:text-gray-400">
            Use <code className="px-1 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-xs">{'{recipientName}'}</code> to personalise for each recipient. A feedback link button is automatically appended.
          </p>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-full hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors"
            >
              Save Template
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailTemplateModal;
